<template>
	<v-container class="dashboard-container">
		<v-row class="contentRow" justify="center">
			<v-col cols="12">
				<div class="steps-wrapper">

					<div class="step-container">
						<div class="step-circle">
							<v-icon size="35" color="trace">fas fa-book-open</v-icon>
						</div>
						<div class="step-text">
							<h4><b>Handbooks</b></h4>
						</div>
					</div>

					<!-- Arrow 1 -->
					<v-col class="arrow-col">
						<v-icon size="25">fas fa-arrow-right-long</v-icon>
					</v-col>

					<div class="step-container">
						<div class="step-circle">
							<v-icon size="35" color="error">fas fa-share-from-square</v-icon>
						</div>
						<div class="step-text">
							<h4><b>Procedure</b></h4>
						</div>
					</div>

					<!-- Arrow 2 -->
					<v-col class="arrow-col">
						<v-icon size="25">fas fa-arrow-right-long</v-icon>
					</v-col>

					<div class="step-container">
						<div class="step-circle">
							<v-icon size="35" color="success">fas fa-chalkboard-user</v-icon>
						</div>
						<div class="step-text">
							<h4><b>Instruction</b></h4>
						</div>
					</div>

					<!-- Arrow 3 -->
					<v-col class="arrow-col">
						<v-icon size="25">fas fa-arrow-right-long</v-icon>
					</v-col>

					<div class="step-container">
						<div class="step-circle">
							<v-icon size="35" color="warning">fas fa-cog</v-icon>
						</div>
						<div class="step-text">
							<h4><b>Control</b></h4>
						</div>
					</div>
				</div>
			</v-col>
		</v-row>

		<v-row justify="center">
			<v-col cols="12">
				<h3>Show how each area is put into practice in your organisation.</h3>
				<p>For each subject below, identify where its requirements are put into practice in your day-to-day operations. Work through the four levels, from broad to specific:
					<ul>
						<li>Handbook: The overarching policy or manual that sets out your organisation’s approach to the subject (e.g. Environmental Management Handbook).</li>
						<li>Procedure: The defined, repeatable process that carries out that policy (e.g. Supplier Evaluation Procedure).</li>
						<li>Instruction: The step-by-step guidance staff follow to complete a specific task (e.g. How to Approve a Supplier).</li>
						<li>Control: The check, review or monitoring activity that confirms the requirements are being met (e.g. Annual Supplier Performance Review).</li>
					</ul>
				</p>
			
				<!-- Add new subject -->
				<div class="add-subject-container">

					<v-text-field
						v-model="newSubject"
						label="Enter a new subject"
						placeholder="e.g. Health & Safety"
						outlined
						dense
						clearable
						hide-details
						class="new-subject-input"
						@keyup.enter="addSubject"
					/>

					<v-btn
						color="#217b7f"
						class="add-subject-btn"
						:disabled="!newSubject || !newSubject.trim()"
						@click="addSubject"
					>
						<v-icon left>fas fa-plus</v-icon>
						Add Subject
					</v-btn>

				</div>

				<!-- Implementation table -->
				<div class="table-wrapper">

					<table class="implementation-table">

						<thead>
							<tr>
								<th class="col-subject">Subject</th>
								<th>Handbook</th>
								<th>Procedure</th>
								<th>Instruction</th>
								<th>Control</th>
								<th class="col-action">Action</th>
							</tr>
						</thead>

						<tbody>

							<tr
								v-for="(row, index) in implementationRows"
								:key="row.ID || row.subject"
							>

								<!-- Subject -->
								<td class="col-subject">
									<strong>{{ row.subject }}</strong>
								</td>

								<!-- Handbook -->
								<td>
									<v-textarea
										v-model="row.Handbook"
										class="impl-field"
										:placeholder="getPlaceholder(row, 'Handbook')"
										:readonly="!row.editing"
										:outlined="row.editing"
										:solo="!row.editing"
										:flat="!row.editing"
										background-color="transparent"
										auto-grow
										rows="1"
										dense
										hide-details
									/>
								</td>

								<!-- Procedure -->
								<td>
									<v-textarea
										v-model="row.Procedure"
										class="impl-field"
										:placeholder="getPlaceholder(row, 'Procedure')"
										:readonly="!row.editing"
										:outlined="row.editing"
										:solo="!row.editing"
										:flat="!row.editing"
										background-color="transparent"
										auto-grow
										rows="1"
										dense
										hide-details
									/>
								</td>

								<!-- Instruction -->
								<td>
									<v-textarea
										v-model="row.Instruction"
										class="impl-field"
										:placeholder="getPlaceholder(row, 'Instruction')"
										:readonly="!row.editing"
										:outlined="row.editing"
										:solo="!row.editing"
										:flat="!row.editing"
										background-color="transparent"
										auto-grow
										rows="1"
										dense
										hide-details
									/>
								</td>

								<!-- Control -->
								<td>
									<v-textarea
										v-model="row.Control"
										class="impl-field"
										:placeholder="getPlaceholder(row, 'Control')"
										:readonly="!row.editing"
										:outlined="row.editing"
										:solo="!row.editing"
										:flat="!row.editing"
										background-color="transparent"
										auto-grow
										rows="1"
										dense
										hide-details
									/>
								</td>

								<!-- Actions -->
								<td class="col-action">

									<v-btn
										icon
										small
										:title="row.editing ? 'Save' : 'Edit'"
										@click="toggleEdit(row)"
									>
										<v-icon small>
											{{ row.editing
												? "fas fa-floppy-disk"
												: "fas fa-pen"
											}}
										</v-icon>
									</v-btn>

									<v-btn
										v-if="row.custom"
										icon
										small
										color="error"
										title="Remove subject"
										@click="removeSubject(index)"
									>
										<v-icon small>
											fas fa-trash
										</v-icon>
									</v-btn>

								</td>

							</tr>

							<!-- Empty state -->
							<tr v-if="implementationRows.length === 0">
								<td colspan="6" class="text-center">
									No subjects available.
								</td>
							</tr>

						</tbody>

					</table>

				</div>

				<!-- Save all -->
				<div class="save-all-container">

					<v-btn
						color="#217b7f"
						class="save-all-btn"
						:loading="savingAll"
						:disabled="savingAll"
						@click="saveAllSubjects"
					>
						<v-icon left>fas fa-floppy-disk</v-icon>
						Save All
					</v-btn>

				</div>

			</v-col>
		</v-row>

	</v-container>
</template>

<script>
import LoadingState from "../consts/LoadingState"
import { riskAPI } from "../api/csr/risk"

export default {
	data() {
		return {
			newSubject: "",
			savingAll: false,
			implementationRows: [
				{
					subject: "Governance & Management",
					Handbook: "",
					Procedure: "",
					Instruction: "",
					Control: "",
					editing: false,
					custom: false
				},
				{
					subject: "People & Human Rights",
					Handbook: "",
					Procedure: "",
					Instruction: "",
					Control: "",
					editing: false,
					custom: false
				},
				{
					subject: "Environment & Resources",
					Handbook: "",
					Procedure: "",
					Instruction: "",
					Control: "",
					editing: false,
					custom: false
				},
				{
					subject: "Operations & Supply Chains",
					Handbook: "",
					Procedure: "",
					Instruction: "",
					Control: "",
					editing: false,
					custom: false
				}
			]
		}
	},
	mounted() {
		this.viewProcedureData()
	},
	methods: {
		getPlaceholder(row, field) {
			const placeholders = {
				"Governance & Management": {
					Handbook: "e.g. Anti-Corruption Handbook",
					Procedure: "e.g. Gifts & Hospitality Procedure",
					Instruction: "e.g. How to Register a Gift",
					Control: "e.g. Quarterly Gift Register Review"
				},

				"People & Human Rights": {
					Handbook: "e.g. Human Rights & Labour Handbook",
					Procedure: "e.g. Employee Grievance Procedure",
					Instruction: "e.g. How to Report a Workplace Concern",
					Control: "e.g. Annual Employee Rights Review"
				},

				"Environment & Resources": {
					Handbook: "e.g. Environmental Management Handbook",
					Procedure: "e.g. Waste Management Procedure",
					Instruction: "e.g. How to Handle Hazardous Waste",
					Control: "e.g. Monthly Waste Compliance Check"
				},

				"Operations & Supply Chains": {
					Handbook: "e.g. Supply Chain Management Handbook",
					Procedure: "e.g. Supplier Evaluation Procedure",
					Instruction: "e.g. How to Approve a Supplier",
					Control: "e.g. Annual Supplier Performance Review"
				}
			}

			return placeholders[row.subject]?.[field] || `e.g. Add ${field.toLowerCase()} details...`
		},
		async viewProcedureData() {
			try {
				const result = await riskAPI.viewProcedures()
				const savedSubjects = Array.isArray(result)
					? (Array.isArray(result[0]) ? result[0] : result)
					: []
				savedSubjects.forEach(saved => {
					if (!saved.Subject) return
					const existing = this.implementationRows.find(
						row => row.subject.toLowerCase() ===
							saved.Subject.toLowerCase()
					)
					if (existing) {
						existing.ID = saved.ID
						existing.Handbook = saved.Handbook || ""
						existing.Procedure = saved.Procedure || ""
						existing.Instruction = saved.Instruction || ""
						existing.Control = saved.Control || ""
					} else {
						this.implementationRows.push({
							ID: saved.ID,
							subject: saved.Subject,
							Handbook: saved.Handbook || "",
							Procedure: saved.Procedure || "",
							Instruction: saved.Instruction || "",
							Control: saved.Control || "",
							editing: false,
							custom: true
						})
					}
				})
			} catch (error) {
				this.$store.dispatch(
					"loading/" + LoadingState.ACTION_SET_ERROR,
					error,
					{ root: true }
				)
			}
		},

		addSubject() {
			const subjectName = this.newSubject.trim()
			if (!subjectName) return
			const exists = this.implementationRows.some(
				row => row.subject.toLowerCase() === subjectName.toLowerCase()
			)
			if (exists) {
				this.$store.dispatch(
					"loading/" + LoadingState.ACTION_SET_ERROR,
					new Error("This subject already exists."),
					{ root: true }
				)
				return
			}

			this.implementationRows.push({
				subject: subjectName,
				Handbook: "",
				Procedure: "",
				Instruction: "",
				Control: "",
				editing: true,
				custom: true
			})
			this.newSubject = ""
		},

		async removeSubject(index) {
			const row = this.implementationRows[index]
			if (!row || !row.custom) return
			if (!confirm(`Are you sure you want to remove "${row.subject}"?`)) {
				return
			}

			if (row.ID) {
				try {
					await riskAPI.deleteProcedure(row.subject)
					this.$store.dispatch(
						"loading/" + LoadingState.ACTION_LOADING_SUCCESS,
						"Successfully removed!",
						{ root: true }
					)
				} catch (error) {
					this.$store.dispatch(
						"loading/" + LoadingState.ACTION_SET_ERROR,
						error,
						{ root: true }
					)
					return
				}
			}

			// Look the row up again in case the list changed during the request
			const currentIndex = this.implementationRows.indexOf(row)
			if (currentIndex !== -1) {
				this.implementationRows.splice(currentIndex, 1)
			}
		},

		toggleEdit(row) {
			if (!row.editing) {
				row.editing = true
				return
			}
			this.saveRow(row)
		},

		async saveRow(row) {
			await this.saveAllSubjects(row)
		},

		async saveAllSubjects() {
			if (this.savingAll) return

			this.savingAll = true

			try {
				const subjects = this.implementationRows.map(item => ({
					Subject: item.subject.trim(),
					Handbook: item.Handbook || "",
					Procedure: item.Procedure || "",
					Instruction: item.Instruction || "",
					Control: item.Control || ""
				}))

				await riskAPI.saveProcedure(subjects)

				// After Save All, all rows should return to view mode
				this.implementationRows.forEach(item => {
					item.editing = false
				})

				this.$store.dispatch(
					"loading/" + LoadingState.ACTION_LOADING_SUCCESS,
					"Successfully saved!",
					{ root: true }
				)

				await this.reloadSavedSubjects()

			} catch (error) {
				this.$store.dispatch(
					"loading/" + LoadingState.ACTION_SET_ERROR,
					error,
					{ root: true }
				)
			} finally {
				this.savingAll = false
			}
		},

		async reloadSavedSubjects() {
			const result = await riskAPI.viewProcedures()
			const savedSubjects = Array.isArray(result)
				? (Array.isArray(result[0]) ? result[0] : result)
				: []
			savedSubjects.forEach(saved => {
				const existing = this.implementationRows.find(
					row => row.subject.toLowerCase() ===
						saved.Subject.toLowerCase()
				)
				if (existing) {
					existing.ID = saved.ID
				}
			})
		}
	}
}
</script>

<style scoped>
.steps-wrapper {
	display: flex;
	justify-content: center;
	align-items: flex-start;
	gap: 24px;
	position: relative;
	z-index: 2;
	padding: 32px 40px;
	border: 2px solid #1e7c83;
	border-radius: 5px;
}

.step-container {
	text-align: center;
	max-width: 100px;
}

.step-circle {
	width: 70px;
	height: 70px;
	border-radius: 50%;
	display: flex;
	align-items: center;
	justify-content: center;
	margin: 0 auto 20px auto;
	backdrop-filter: blur(10px);
	transition: all 0.3s ease;
	box-shadow: 0 8px 16px rgba(0, 0, 0, 0.2);
}

.step-circle:hover {
	transform: translateY(-2px);
}

.arrow-col {
	flex: 0 0 auto;
	width: auto;
	max-width: none;
	height: 70px;
	display: flex;
	align-items: center;
	justify-content: center;
	margin: 0;
	padding: 0 8px;
}

/* Add subject */
.add-subject-container {
	display: flex;
	justify-content: flex-end;
	align-items: center;
	gap: 12px;
	margin-top: 20px;
	margin-bottom: 15px;
}

.new-subject-input {
	max-width: 280px;
}

.add-subject-btn {
	color: white !important;
	height: 40px !important;
}

/* Table */
.table-wrapper {
	overflow-x: auto;
	margin-top: 16px;
}

.implementation-table {
	width: 100%;
	min-width: 1000px;
	border-collapse: collapse;
	background-color: #f2f7f2;
	table-layout: fixed;
}

.implementation-table th,
.implementation-table td {
	text-align: left;
	padding: 12px;
	border: 2px solid #ffffff;
	font-size: 14px;
	vertical-align: middle;
}

.implementation-table th {
	font-weight: 600;
	background-color: #e5efeb;
}

.implementation-table .col-subject {
	width: 19%;
}

.implementation-table .col-action {
	width: 90px;
	text-align: center;
}

.impl-field {
	width: 100%;
	min-width: 0;
	margin: 0;
	padding: 0;
}

.impl-field >>> textarea {
	font-size: 13px !important;
	line-height: 1.5;
}

.save-all-container {
	display: flex;
	justify-content: flex-end;
	margin-top: 20px;
	margin-bottom: 20px;
}

.save-all-btn {
	color: white !important;
	padding: 5px 20px;
}

@media (max-width: 768px) {
	.steps-wrapper {
		flex-direction: column;
		align-items: center;
		gap: 10px;
		padding: 24px;
	}

	.arrow-col {
		height: auto;
		padding: 10px 0;
	}

	.arrow-col .v-icon {
		transform: rotate(90deg);
	}

	.step-container {
		max-width: 200px;
	}

	.add-subject-container {
		flex-direction: column;
		align-items: stretch;
	}

	.new-subject-input {
		max-width: 100%;
	}

	.add-subject-btn {
		width: 100%;
	}
}

@media (max-width: 1024px) and (min-width: 769px) {
	.steps-wrapper {
		padding: 24px;
		gap: 12px;
	}

	.step-circle .v-icon {
		font-size: 30px !important;
	}
}

@media (min-width: 1025px) {
	.steps-wrapper {
		padding: 32px 48px;
	}
}
</style>